import { __internal_useStatementQuery } from '@clerk/shared/react/index';

import { Alert } from '@/ui/elements/Alert';
import { Header } from '@/ui/elements/Header';
import { ProfileCard } from '@/ui/elements/ProfileCard';
import { getDiscountDescription } from '@/ui/utils/billing';
import { formatDate } from '@/ui/utils/formatDate';

import { useSubscriberTypeContext, useSubscriberTypeLocalizationRoot } from '../../contexts/components';
import {
  Box,
  descriptors,
  Icon,
  localizationKeys,
  SimpleButton,
  Span,
  Spinner,
  useLocalizations,
} from '../../customizables';
import { ArrowRight, Plus, RotateLeftRight } from '../../icons';
import { useRouter } from '../../router';
import { Statement } from './Statement';

export const StatementPage = () => {
  const { params, navigate } = useRouter();
  const subscriberType = useSubscriberTypeContext();
  const localizationRoot = useSubscriberTypeLocalizationRoot();
  const { t, translateError, $ } = useLocalizations();
  const requesterType = subscriberType === 'organization' ? 'organization' : 'user';

  const {
    data: statement,
    isLoading,
    error,
  } = __internal_useStatementQuery({
    statementId: params.statementId,
    for: requesterType,
    enabled: Boolean(params.statementId),
  });

  if (isLoading) {
    return (
      <ProfileCard.Page>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Spinner
            colorScheme='primary'
            sx={{ margin: 'auto', display: 'block' }}
            elementDescriptor={descriptors.spinner}
          />
        </Box>
      </ProfileCard.Page>
    );
  }

  return (
    <ProfileCard.Page>
      <Header.Root
        sx={t => ({
          borderBlockEndWidth: t.borderWidths.$normal,
          borderBlockEndStyle: t.borderStyles.$solid,
          borderBlockEndColor: t.colors.$borderAlpha100,
          marginBlockEnd: t.space.$4,
          paddingBlockEnd: t.space.$4,
        })}
      >
        <Header.BackLink
          onClick={() => void navigate('../../', { searchParams: new URLSearchParams('tab=statements') })}
        >
          <Header.Title
            localizationKey={localizationKeys(`${localizationRoot}.billingPage.statementsSection.title`)}
            textVariant='h2'
          />
        </Header.BackLink>
      </Header.Root>
      {!statement ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Alert
            variant='danger'
            colorScheme='danger'
          >
            {error
              ? translateError(error.errors[0])
              : t(localizationKeys(`${localizationRoot}.billingPage.statementsSection.notFound`))}
          </Alert>
        </Box>
      ) : (
        <Statement.Root>
          <Statement.Header
            title={formatDate(statement.timestamp, 'monthyear')}
            id={statement.id}
            status={statement.status}
          />
          <Statement.Body>
            {statement.groups.map(group => (
              <Statement.Section key={group.timestamp.toISOString()}>
                <Statement.SectionHeader text={formatDate(group.timestamp, 'long')} />
                <Statement.SectionContent>
                  {group.items.map(item => (
                    <Statement.SectionContentItem key={item.id}>
                      <Statement.SectionContentDetailsHeader
                        title={item.subscriptionItem.plan.name}
                        description={`${item.subscriptionItem.amount ? $(item.subscriptionItem.amount) : ''} / ${item.subscriptionItem.planPeriod === 'month' ? t(localizationKeys('billing.month')) : t(localizationKeys('billing.year'))}`}
                        secondaryTitle={$(item.amount)}
                      />
                      <Statement.SectionContentDetailsList>
                        <Statement.SectionContentDetailsListItem
                          label={
                            item.chargeType === 'recurring'
                              ? localizationKeys(
                                  `${localizationRoot}.billingPage.statementsSection.itemCaption__paidForPlan`,
                                  {
                                    plan: item.subscriptionItem.plan.name,
                                    period: item.subscriptionItem.planPeriod,
                                  },
                                )
                              : localizationKeys(
                                  `${localizationRoot}.billingPage.statementsSection.itemCaption__subscribedAndPaidForPlan`,
                                  {
                                    plan: item.subscriptionItem.plan.name,
                                    period: item.subscriptionItem.planPeriod,
                                  },
                                )
                          }
                          labelIcon={item.chargeType === 'recurring' ? RotateLeftRight : Plus}
                          value={
                            <SimpleButton
                              onClick={() => void navigate(`../../payment-attempt/${item.id}`)}
                              variant='link'
                              colorScheme='primary'
                              textVariant='buttonSmall'
                              sx={t => ({
                                gap: t.space.$1,
                              })}
                            >
                              <Span localizationKey={localizationKeys('billing.viewPayment')} />
                              <Icon
                                icon={ArrowRight}
                                size='sm'
                                aria-hidden
                              />
                            </SimpleButton>
                          }
                        />
                        {item.totals?.discounts?.proration && item.totals.discounts.proration.amount.amount > 0 ? (
                          <Statement.SectionContentDetailsListItem
                            label={localizationKeys('billing.proratedDiscount')}
                            value={`(${$(item.totals.discounts.proration.amount)})`}
                          />
                        ) : null}
                        {item.totals?.discounts?.discount && item.totals.discounts.discount.amount.amount > 0 ? (
                          <Statement.SectionContentDetailsListItem
                            label={`${item.totals.discounts.discount.name} ${getDiscountDescription(
                              item.totals.discounts.discount,
                              item.totals.discounts.discount.durationInCycles,
                              item.subscriptionItem.planPeriod,
                              { $, t },
                            )}`}
                            value={`(${$(item.totals.discounts.discount.amount)})`}
                          />
                        ) : null}
                        {item.subscriptionItem.credits &&
                        item.subscriptionItem.credits.proration &&
                        item.subscriptionItem.credits.proration.amount.amount > 0 ? (
                          <Statement.SectionContentDetailsListItem
                            label={localizationKeys(
                              `${localizationRoot}.billingPage.statementsSection.itemCaption__proratedCredit`,
                            )}
                            value={`(${$(item.subscriptionItem.credits.proration.amount)})`}
                          />
                        ) : null}
                        {item.subscriptionItem.credits &&
                        item.subscriptionItem.credits.payer &&
                        item.subscriptionItem.credits.payer.appliedAmount.amount > 0 ? (
                          <Statement.SectionContentDetailsListItem
                            label={localizationKeys(
                              `${localizationRoot}.billingPage.statementsSection.itemCaption__payerCredit`,
                            )}
                            value={`(${$(item.subscriptionItem.credits.payer.appliedAmount)})`}
                          />
                        ) : null}
                      </Statement.SectionContentDetailsList>
                    </Statement.SectionContentItem>
                  ))}
                </Statement.SectionContent>
              </Statement.Section>
            ))}
          </Statement.Body>
          <Statement.Footer
            label={localizationKeys(`${localizationRoot}.billingPage.statementsSection.totalPaid`)}
            value={$(statement.totals.grandTotal)}
          />
        </Statement.Root>
      )}
    </ProfileCard.Page>
  );
};
